import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';

import DOMPurify from 'dompurify';

import { OutlookDataService } from '../../services/outlook-data.service';
import { IOutlookSignatureCandidate } from '../../models/IOutlookSignature.interface';
import { signatureHtmlToText } from '../../utils/signature-extract.util';

/**
 * Email signature candidates.
 *
 * The honest framing matters here and is deliberate: Microsoft Graph does not expose the Outlook
 * signature — see the long note at the top of signature-extract.util.ts. So this tab does not claim
 * to have found "your signature". It shows where each candidate came from and how much to trust it,
 * lets the user edit it, and copies it out. Copy is the primary action because pasting into the OWA
 * signature editor is what actually gets a signature into Outlook.
 */
@Component({
  selector: 'app-outlook-signature-tab',
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './outlook-signature-tab.component.html',
  styleUrls: ['./outlook-signature-tab.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OutlookSignatureTabComponent implements OnInit {
  private readonly data = inject(OutlookDataService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  candidates: IOutlookSignatureCandidate[] = [];
  loading = false;

  selectedIndex = 0;
  /** Editable working copy — edits must not mutate the cached candidate. */
  draftHtml = '';
  editing = false;

  ngOnInit(): void {
    this.load();
  }

  refresh(): void {
    this.load(true);
  }

  select(index: number): void {
    this.selectedIndex = index;
    this.draftHtml = this.candidates[index]?.html ?? '';
    this.editing = false;
    this.cdr.markForCheck();
  }

  get selected(): IOutlookSignatureCandidate | null {
    return this.candidates[this.selectedIndex] ?? null;
  }

  /** Sanitised preview for the sandboxed iframe — same treatment as a message body. */
  get preview(): SafeHtml {
    const clean = DOMPurify.sanitize(this.draftHtml, {
      WHOLE_DOCUMENT: true,
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form']
    });
    return this.sanitizer.bypassSecurityTrustHtml(
      `<style>body{margin:0;padding:12px;font-family:Lato,Segoe UI,Arial,sans-serif;}` +
        `img{max-width:100%;height:auto;}</style>${clean}`
    );
  }

  toggleEdit(): void {
    this.editing = !this.editing;
  }

  resetDraft(): void {
    this.draftHtml = this.selected?.html ?? '';
    this.cdr.markForCheck();
  }

  /**
   * Copies both an HTML and a plain-text flavour.
   *
   * The HTML flavour is the point: OWA's signature editor accepts a rich paste and keeps the
   * formatting, whereas a text-only clipboard would arrive as a flat block. The plain-text flavour
   * rides along for editors that only take text.
   */
  async copy(): Promise<void> {
    const html = this.draftHtml;
    const text = signatureHtmlToText(html);

    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' })
          })
        ]);
      } else {
        // Firefox has historically lacked ClipboardItem; plain text is a lesser but working result.
        await navigator.clipboard.writeText(text);
      }
      this.snackBar.open('Signature copied — paste it into Outlook’s signature editor.', 'Close', {
        duration: 4000
      });
    } catch {
      this.snackBar.open('Could not copy. Select the text and copy it manually.', 'Close', { duration: 4000 });
    }
  }

  openOutlookSignatureSettings(): void {
    window.open('https://outlook.office.com/mail/options/accounts-category/signatures-subcategory', '_blank', 'noopener');
  }

  private load(force = false): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.data
      .getSignatureCandidates(force)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((candidates) => {
        this.candidates = candidates;
        this.selectedIndex = 0;
        this.draftHtml = candidates[0]?.html ?? '';
        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
